<script setup lang="ts">
import { isJust, just, matchMaybe, nothing, type Maybe } from "./lib/maybe";
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import ZwDialog from "./components/ui/ZwDialog.vue";
import ZwButton from "./components/ui/ZwButton.vue";
import { installBeaconPolling } from "./lib/beacon";
import { installCloudAuthDeepLink } from "./lib/cloudAuthDeepLink";
import { installCloudBackupListener } from "./lib/cloudBackup";
import {
  installCloudSyncBeforeCommitListener,
  registerCloudDivergencePrompt,
} from "./lib/cloudSync";
import { isAllowedHost } from "./lib/hostGate";
import {
  getWindowKind,
  isMobileShell,
  NAVIGATE_EVENT,
  type NavigatePayload,
  type WindowKind,
} from "./lib/tauri/core";
import { isTauri } from "./lib/runtime";
import {
  installUiZoomShortcuts,
  zoomIn,
  zoomOut,
  resetZoom,
} from "./composables/useUiZoom";
import { installKeyboardInset } from "./composables/useKeyboardInset";
import { useShellWidth } from "./composables/useShellWidth";
import { narrowLayoutForWidth } from "./lib/narrowLayout";
import { listenMenuCommands } from "./lib/menuCommand";
import { initialCloudEditorRoute } from "./lib/cloudEditorHost";

type Cleanup = () => void;

const allowedHost = isAllowedHost();
const router = useRouter();
let unlistenDeepLink: Maybe<Cleanup> = nothing;
let unlistenBackup: Maybe<Cleanup> = nothing;
let unlistenSync: Maybe<Cleanup> = nothing;
let stopBeaconPolling: Maybe<Cleanup> = nothing;
let unlistenUiZoom: Maybe<Cleanup> = nothing;
let unlistenMenuCommands: Maybe<UnlistenFn> = nothing;
let unlistenNavigate: Maybe<UnlistenFn> = nothing;
let uninstallKeyboardInset: Maybe<Cleanup> = nothing;

const divergenceOpen = ref(false);
let divergenceResolver: Maybe<(choice: "keep_local" | "use_cloud") => void> =
  nothing;
const isMobileShellSync = ref(false);

/**
 * Narrow window (ZW-276). Sets `html[data-zw-narrow="1"]` so the phone-layout
 * CSS (settings dialog, status dock, dock panels) also applies to a narrow
 * desktop window or browser. Width only: `data-zw-mobile-shell` stays the
 * native-shell flag, which the soft-keyboard inset and the cloud divergence
 * prompt rely on.
 */
const narrowWindow = ref(
  typeof window === "undefined"
    ? false
    : narrowLayoutForWidth(false, window.innerWidth),
);
useShellWidth(
  () =>
    typeof document === "undefined" ? nothing : just(document.documentElement),
  (width) => {
    narrowWindow.value = narrowLayoutForWidth(narrowWindow.value, width);
  },
);
watch(
  narrowWindow,
  (narrow) => {
    if (typeof document === "undefined") return;
    if (narrow) document.documentElement.dataset.zwNarrow = "1";
    else delete document.documentElement.dataset.zwNarrow;
  },
  { immediate: true },
);

function runCleanup(m: Maybe<Cleanup | UnlistenFn>): void {
  matchMaybe(m, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
}

function resolveDivergence(choice: "keep_local" | "use_cloud"): void {
  divergenceOpen.value = false;
  matchMaybe(divergenceResolver, {
    just: (resolve) => {
      resolve(choice);
    },
    nothing: () => {},
  });
  divergenceResolver = nothing;
}

const search: Maybe<URLSearchParams> =
  typeof window !== "undefined"
    ? just(new URLSearchParams(window.location.search))
    : nothing;
/**
 * Web demo: `#/?demo=1&legacy=1` opens the sample project as pre-conversion
 * Markdown, so opening it runs the `.zwdoc` conversion (#259). Read from
 * the boot hash (main.ts keeps its query) because the router hasn't settled
 * when the initial route is chosen.
 */
const bootLegacy =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.hash.split("?")[1] ?? "").get(
    "legacy",
  ) === "1";
/** Browser-only: `?splash` / `?ui` force design surfaces for preview. */
const forceSplash = matchMaybe(search, {
  just: (s) => s.has("splash"),
  nothing: () => false,
});
const forceUi = matchMaybe(search, {
  just: (s) => s.has("ui"),
  nothing: () => false,
});
/** Marketing iframe: `?embed=1` or nested frame. */
const isEmbed =
  matchMaybe(search, {
    just: (s) => s.has("embed"),
    nothing: () => false,
  }) ||
  (typeof window !== "undefined" && window.self !== window.top);

/** Initial route for a given `WindowKind`, resolved once at boot. Nothing means
 * stay on the "Loading…" boot route (only reachable for `"unknown"`, an
 * effectively-unused desktop safety fallback). */
function initialRouteFor(kind: WindowKind): Maybe<string> {
  if (kind === "splash") return just("/splash");
  if (kind === "about") return just("/about");
  if (kind === "editor") return just("/");
  if (kind === "web") {
    const cloudEditor = initialCloudEditorRoute();
    if (isJust(cloudEditor)) return cloudEditor;
    // try.zephyrwrite.com (and local web) open the demo manuscript by default.
    // Splash remains available via `?splash` (see onMounted forceSplash).
    // `legacy=1` keeps the demo as Markdown so it converts on open (#259).
    return just(bootLegacy ? "/?demo=1&legacy=1" : "/?demo=1");
  }
  return nothing;
}

if (typeof document !== "undefined" && isEmbed) {
  document.documentElement.classList.add("zw-embed");
  // Marketing hero uses the dark band; match that in the iframe.
  document.documentElement.classList.add("dark");
  document.documentElement.dataset.zwEmbed = "1";
}

onMounted(async () => {
  if (allowedHost === false) return;

  if (await isMobileShell()) {
    document.documentElement.dataset.zwMobileShell = "1";
    isMobileShellSync.value = true;
    // Only the mobile shell has a soft keyboard; on desktop the measurement
    // would just pick up pinch-zoom noise.
    uninstallKeyboardInset = just(installKeyboardInset());
  }

  unlistenUiZoom = just(installUiZoomShortcuts());

  if (forceUi) {
    void router.replace("/ui");
  } else if (forceSplash) {
    void router.replace("/splash");
  } else {
    const initial = initialRouteFor(await getWindowKind());
    if (isJust(initial)) {
      void router.replace(initial.value);
    }
  }

  if (isTauri()) {
    try {
      unlistenNavigate = just(
        await listen<NavigatePayload>(NAVIGATE_EVENT, (event) => {
          const next = event.payload?.kind;
          // `navigate_to_splash` resets shared editor state. Treat it as a hard
          // reset, not a back-step, so `replace` (not `push`).
          if (next === "splash") {
            void router.replace("/splash");
          } else if (next === "editor") {
            void router.push("/");
          }
        }),
      );
    } catch (error) {
      console.warn("Navigate listener unavailable", error);
    }
    try {
      unlistenMenuCommands = just(
        await listenMenuCommands({
          "view-zoom-in": () => zoomIn(),
          "view-zoom-out": () => zoomOut(),
          "view-zoom-reset": () => resetZoom(),
        }),
      );
    } catch (error) {
      console.warn("Menu command listener unavailable", error);
    }
    registerCloudDivergencePrompt(
      () =>
        new Promise<"keep_local" | "use_cloud">((resolve) => {
          divergenceResolver = just(resolve);
          divergenceOpen.value = true;
        }),
    );
    // Auth deep links must be handled at the root so a magic-link return works
    // even when the Settings window is not open.
    unlistenDeepLink = just(await installCloudAuthDeepLink());
    unlistenBackup = just(await installCloudBackupListener());
    unlistenSync = just(await installCloudSyncBeforeCommitListener());
    stopBeaconPolling = just(installBeaconPolling());
  }
});

onUnmounted(() => {
  registerCloudDivergencePrompt(null);
  delete document.documentElement.dataset.zwMobileShell;
  delete document.documentElement.dataset.zwNarrow;
  runCleanup(uninstallKeyboardInset);
  uninstallKeyboardInset = nothing;
  runCleanup(unlistenUiZoom);
  unlistenUiZoom = nothing;
  runCleanup(unlistenMenuCommands);
  unlistenMenuCommands = nothing;
  runCleanup(unlistenDeepLink);
  unlistenDeepLink = nothing;
  runCleanup(unlistenBackup);
  unlistenBackup = nothing;
  runCleanup(unlistenSync);
  unlistenSync = nothing;
  runCleanup(stopBeaconPolling);
  stopBeaconPolling = nothing;
  runCleanup(unlistenNavigate);
  unlistenNavigate = nothing;
});
</script>

<template>
  <div v-if="allowedHost === false" class="host-gate">
    <p class="host-gate-brand">Zephyrwrite</p>
    <p class="host-gate-copy">
      This demo only runs on
      <a href="https://zephyrwrite.com">zephyrwrite.com</a>
      or in the desktop app.
    </p>
  </div>
  <router-view v-else />

  <ZwDialog
    :open="divergenceOpen"
    title="Cloud backup differs"
    :description="
      just(
        isMobileShellSync
          ? 'This project was edited elsewhere. Keep what’s on this phone, or use the cloud version?'
          : 'This project was edited on another device. Keep this computer’s version, or use the cloud version?',
      )
    "
    size="sm"
    :close-on-backdrop="false"
    :show-close="false"
    :z-index="400"
  >
    <template #footer>
      <div class="cloud-divergence-actions">
        <ZwButton @click="resolveDivergence('keep_local')">
          {{
            isMobileShellSync
              ? "Keep this phone’s version"
              : "Keep this computer’s version"
          }}
        </ZwButton>
        <ZwButton variant="primary" @click="resolveDivergence('use_cloud')">
          Use the cloud version
        </ZwButton>
      </div>
    </template>
  </ZwDialog>
</template>

<style scoped>
.cloud-divergence-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: flex-end;
}

.host-gate {
  display: grid;
  place-content: center;
  gap: 0.75rem;
  height: 100%;
  padding: 2rem;
  text-align: center;
  background: var(--bg);
  color: var(--text);
  font-family: var(--font-ui);
}

.host-gate-brand {
  margin: 0;
  font-family: var(--font-heading, Literata, Georgia, serif);
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-strong);
}

.host-gate-copy {
  margin: 0;
  max-width: 22rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-muted);
}

.host-gate-copy a {
  color: var(--text-strong);
  text-underline-offset: 0.15em;
}
</style>
